"use client";

/**
 * TrackWaveform — canvas-based audio waveform visualiser + stereo VU meters.
 *
 * • Decodes the full audio file via /api/audio-proxy (bypasses CORS).
 * • Extracts REAL per-channel amplitude peaks from the decoded AudioBuffer.
 * • Draws the waveform in one of four styles selectable by the user:
 *     bars     – centred symmetric bars (default)
 *     mirror   – SoundCloud-style: bars mirrored top + bottom
 *     filled   – smooth filled bezier area
 *     line     – thin bezier curve (oscilloscope)
 * • Live playback cursor and comment pins.
 * • Stereo LED-style VU meters driven by actual decoded amplitude at current
 *   playback position — all animation via refs so the RAF loop never restarts.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useMediaPlayer } from "@/contexts/media-player-context";
import { type TrackComment, formatCommentTimestamp } from "@/services/track-comments";
import { cn } from "@/lib/utils";
import { AudioLines, Loader2 } from "lucide-react";
import { getMemWaveform, getIdbWaveform, saveWaveform } from "@/lib/waveform-cache";

// ─── proxy helper ──────────────────────────────────────────────────────────────
function toProxyUrl(url: string): string {
  if (!url) return url;
  // Proxy all remote URLs through our CORS-enabled audio-proxy so
  // waveform decode (fetch + decodeAudioData) works cross-origin.
  const isRemote = url.startsWith('http://') || url.startsWith('https://');
  if (isRemote) {
    return `/api/audio-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

// ─── VU meter constants (matched to main VuMeters component) ──────────────────
const VU_SEGMENTS   = 28;
const PEAK_HOLD_MS  = 2000;
const PEAK_FALL_RATE = 0.5;   // VU/frame (same speed as main meter)

// Calibration: 0 VU = −18 dBFS (EBU broadcast standard)
const VU_REF_DBFS = -18;
const VU_MIN_VU   = -20;
const VU_MAX_VU   =   3;
const VU_SPAN     = VU_MAX_VU - VU_MIN_VU; // 23 dB

function segmentToVu(s: number) {
  return VU_MIN_VU + (s / (VU_SEGMENTS - 1)) * VU_SPAN;
}
function segmentColor(vu: number, lit: boolean): string {
  if (!lit) return "rgba(20,20,20,0.85)";
  if (vu >= 3)   return "#ff2020";   // red — clipping
  if (vu >= 0)   return "#ff8800";   // orange — above ref
  if (vu >= -3)  return "#ffe020";   // yellow — near ref
  if (vu >= -10) return "#a0e020";   // yellow-green
  return "#20e060";                   // green — safe
}

// ─── Waveform style ────────────────────────────────────────────────────────────
export type WaveformStyle = "bars" | "mirror" | "filled" | "line";

const STYLE_LABELS: { style: WaveformStyle; label: string; title: string }[] = [
  { style: "bars",   label: "|||", title: "Bars — centred symmetric bars" },
  { style: "mirror", label: "≋",   title: "Mirror — SoundCloud-style reflection" },
  { style: "filled", label: "◿",   title: "Filled — smooth filled area" },
  { style: "line",   label: "⁞",   title: "Stems — lollipop stem plot" },
];

// ─── Props ─────────────────────────────────────────────────────────────────────
interface TrackWaveformProps {
  audioUrl: string;
  durationSeconds?: number;
  comments?: TrackComment[];
  className?: string;
  height?: number;
  showVu?: boolean;
  vuHeight?: number;
  /** Initial waveform style (user can change it interactively) */
  defaultStyle?: WaveformStyle;
  /** Hide the style-picker buttons (useful in compact bar mode) */
  showStylePicker?: boolean;
  /** Don't download the audio for the waveform until this track actually
   *  plays — a cued track must not cost a visitor the whole file (the player
   *  bar pulled 7.8 MB on every first visit; PageSpeed 2026-10-31). Cached
   *  waveforms still show at once. */
  deferUntilPlayed?: boolean;
}

type DecodeState = "idle" | "loading" | "ready" | "error";

// ─── Component ─────────────────────────────────────────────────────────────────
export function TrackWaveform({
  audioUrl,
  durationSeconds,
  comments = [],
  className,
  height = 80,
  showVu = true,
  vuHeight = 80,
  defaultStyle = "bars",
  showStylePicker = true,
  deferUntilPlayed = false,
}: TrackWaveformProps) {
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);
  const vuCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const waveRafRef = useRef<number>(0);
  const vuRafRef = useRef<number>(0);

  // Additional panel canvas + RAF refs
  const specCanvasRef  = useRef<HTMLCanvasElement>(null);
  const scopeCanvasRef = useRef<HTMLCanvasElement>(null);
  const needleCanvasRef = useRef<HTMLCanvasElement>(null);

  // Decoded peaks
  const peaksLRef = useRef<Float32Array | null>(null);
  const peaksRRef = useRef<Float32Array | null>(null);

  // ── Waveform style — kept in ref so draw functions stay stable ────────────
  const [waveStyle, setWaveStyle] = useState<WaveformStyle>(defaultStyle);
  const waveStyleRef = useRef<WaveformStyle>(defaultStyle);
  useEffect(() => { waveStyleRef.current = waveStyle; }, [waveStyle]);

  // ── Playback state kept in refs so draw functions never go stale ──────────
  const playedSecondsRef = useRef(0);
  const totalSecondsRef = useRef(0);
  const isThisTrackRef = useRef(false);
  const isPlayingRef = useRef(false);
  const commentsRef = useRef(comments);
  useEffect(() => { commentsRef.current = comments; }, [comments]);

  // VU smoothing + peak-hold (persist across frames) — in VU units
  const peakLRef = useRef({ db: VU_MIN_VU, heldAt: 0 });
  const peakRRef = useRef({ db: VU_MIN_VU, heldAt: 0 });
  const vuLRef = useRef(VU_MIN_VU);
  const vuRRef = useRef(VU_MIN_VU);

  const [decodeState, setDecodeState] = useState<DecodeState>("idle");

  // ── Meter mode tab ──────────────────────────────────────────────────────────
  type VuMode = "vu" | "spectrum" | "scope" | "needle";
  const [vuMode, setVuMode] = useState<VuMode>("vu");
  const vuModeRef = useRef<VuMode>("vu");
  useEffect(() => { vuModeRef.current = vuMode; }, [vuMode]);

  const { currentTime, duration, seek, currentTrack, isActive, isPlaying,
          analyserL, analyserR, isVisualizerActive } =
    useMediaPlayer();

  // Analyser refs so drawVu (no-dep callback) can read live data
  const analyserLRef = useRef<AnalyserNode | null>(null);
  const analyserRRef = useRef<AnalyserNode | null>(null);
  const isVisualizerActiveRef = useRef(false);
  useEffect(() => { analyserLRef.current = analyserL; }, [analyserL]);
  useEffect(() => { analyserRRef.current = analyserR; }, [analyserR]);
  useEffect(() => { isVisualizerActiveRef.current = isVisualizerActive; }, [isVisualizerActive]);

  const isThisTrack =
    isActive && !!currentTrack?.audioUrl && currentTrack.audioUrl === audioUrl;
  // Deferred waveforms load once THIS url has played. Two effects used to
  // fight here: "allow" ran only when isThisTrack/isPlaying changed, "revoke"
  // on every url change — so when the player moved to the next track while
  // already playing (both still true), the revoke won and the footer showed
  // the placeholder icon until pause/play (Andrés 2026-11-01, "Smooth Sway").
  const [playedUrl, setPlayedUrl] = useState<string | null>(null);
  useEffect(() => { if (isThisTrack && isPlaying) setPlayedUrl(audioUrl); }, [isThisTrack, isPlaying, audioUrl]);
  const mayFetch = !deferUntilPlayed || playedUrl === audioUrl;
  const playedSeconds = isThisTrack ? currentTime : 0;
  const totalSeconds = isThisTrack
    ? duration || durationSeconds || 0
    : durationSeconds || 0;

  // Sync reactive state → refs on every render
  useEffect(() => { playedSecondsRef.current = playedSeconds; }, [playedSeconds]);
  useEffect(() => { totalSecondsRef.current = totalSeconds; }, [totalSeconds]);
  useEffect(() => { isThisTrackRef.current = isThisTrack; }, [isThisTrack]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  // ── Decode audio buffer via proxy (real amplitude data) ───────────────────
  // Two-level cache: in-memory Map (instant, per session) → IndexedDB (persists
  // across reloads) → full network fetch + Web Audio decode (first visit only).
  useEffect(() => {
    if (!audioUrl) return;
    let cancelled = false;

    async function decode() {
      // ── Level 1: memory cache (synchronous, instant) ──────────────────────
      const memHit = getMemWaveform(audioUrl);
      if (memHit) {
        peaksLRef.current = memHit.peaksL;
        peaksRRef.current = memHit.peaksR;
        setDecodeState("ready");
        return;
      }

      // ── Show loading spinner only when we actually need to wait ───────────
      setDecodeState("loading");
      peaksLRef.current = null;
      peaksRRef.current = null;

      // ── Level 2: IndexedDB cache (fast, no network/decode needed) ─────────
      const idbHit = await getIdbWaveform(audioUrl);
      if (cancelled) return;
      if (idbHit) {
        peaksLRef.current = idbHit.peaksL;
        peaksRRef.current = idbHit.peaksR;
        setDecodeState("ready");
        return;
      }

      // ── Level 3: full fetch + Web Audio decode (first time only) ──────────
      if (!mayFetch) { setDecodeState("idle"); return; }
      try {
        const proxyUrl = toProxyUrl(audioUrl);
        const resp = await fetch(proxyUrl);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const arrayBuffer = await resp.arrayBuffer();
        if (cancelled) return;

        const audioCtx = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        audioCtx.close();
        if (cancelled) return;

        const NUM_BARS = 200;
        const nChannels = audioBuffer.numberOfChannels;
        const ch0 = audioBuffer.getChannelData(0);
        const ch1 = nChannels > 1 ? audioBuffer.getChannelData(1) : ch0;
        const samplesPerBar = Math.floor(ch0.length / NUM_BARS);

        const peaksL = new Float32Array(NUM_BARS);
        const peaksR = new Float32Array(NUM_BARS);

        for (let i = 0; i < NUM_BARS; i++) {
          const start = i * samplesPerBar;
          let maxL = 0, maxR = 0;
          for (let j = start; j < start + samplesPerBar && j < ch0.length; j++) {
            const absL = Math.abs(ch0[j]);
            const absR = Math.abs(ch1[j]);
            if (absL > maxL) maxL = absL;
            if (absR > maxR) maxR = absR;
          }
          peaksL[i] = maxL;
          peaksR[i] = maxR;
        }

        // Normalise both channels by shared max so relative levels are preserved
        let globalMax = 0;
        for (let i = 0; i < NUM_BARS; i++) {
          if (peaksL[i] > globalMax) globalMax = peaksL[i];
          if (peaksR[i] > globalMax) globalMax = peaksR[i];
        }
        if (globalMax > 0) {
          for (let i = 0; i < NUM_BARS; i++) {
            peaksL[i] /= globalMax;
            peaksR[i] /= globalMax;
          }
        }

        if (cancelled) return;

        // Persist to both caches so next visit is instant
        saveWaveform(audioUrl, peaksL, peaksR);

        peaksLRef.current = peaksL;
        peaksRRef.current = peaksR;
        setDecodeState("ready");
      } catch (err) {
        console.warn("TrackWaveform: decode failed", err);
        if (!cancelled) setDecodeState("error");
      }
    }

    decode();
    return () => { cancelled = true; };
  }, [audioUrl, mayFetch]);

  // ── Waveform draw — reads from refs, no reactive deps ────────────────────
  const drawWaveform = useCallback(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas || !peaksLRef.current) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const peaks = peaksLRef.current;
    const numBars = peaks.length;

    const ps = playedSecondsRef.current;
    const ts = totalSecondsRef.current;
    const fraction = ts > 0 ? Math.min(ps / ts, 1) : 0;
    const playedBars = Math.floor(fraction * numBars);

    const style2 = getComputedStyle(document.documentElement);
    const primaryH = style2.getPropertyValue("--primary").trim() || "262 83% 58%";
    const mutedH = style2.getPropertyValue("--muted-foreground").trim() || "215 20% 65%";

    const colorPlayed = primaryH.includes(" ") ? `hsl(${primaryH})` : "#a855f7";
    const colorUnplayed = mutedH.includes(" ") ? `hsl(${mutedH} / 0.4)` : "rgba(156,163,175,0.4)";
    const colorPlayedAlpha = primaryH.includes(" ") ? `hsl(${primaryH} / 0.3)` : "rgba(168,85,247,0.3)";

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentStyle = waveStyleRef.current;

    if (currentStyle === "bars") {
      // ── Classic centred symmetric bars ─────────────────────────────────────
      const barWidth = W / numBars;
      const gap = Math.max(0.5, barWidth * 0.15);
      const bw = barWidth - gap;
      for (let i = 0; i < numBars; i++) {
        const amplitude = peaks[i];
        const barH = Math.max(2, amplitude * H * 0.85);
        const x = i * barWidth;
        const y = (H - barH) / 2;
        ctx.fillStyle = i < playedBars ? colorPlayed : colorUnplayed;
        ctx.beginPath();
        ctx.roundRect(x + gap / 2, y, Math.max(bw, 1), barH, 2);
        ctx.fill();
      }

    } else if (currentStyle === "mirror") {
      // ── SoundCloud mirror: bars grow from centre outward (top + bottom) ────
      const barWidth = W / numBars;
      const gap = Math.max(0.5, barWidth * 0.15);
      const bw = barWidth - gap;
      const mid = H / 2;
      for (let i = 0; i < numBars; i++) {
        const amplitude = peaks[i];
        const half = Math.max(1.5, amplitude * mid * 0.9);
        const x = i * barWidth + gap / 2;
        ctx.fillStyle = i < playedBars ? colorPlayed : colorUnplayed;
        // top half (upward from center)
        ctx.beginPath();
        ctx.roundRect(x, mid - half, Math.max(bw, 1), half, [2, 2, 0, 0]);
        ctx.fill();
        // bottom half (downward from center) — slightly lighter
        ctx.globalAlpha = 0.55;
        ctx.beginPath();
        ctx.roundRect(x, mid, Math.max(bw, 1), half, [0, 0, 2, 2]);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      // Centre line
      ctx.fillStyle = "rgba(128,128,128,0.2)";
      ctx.fillRect(0, mid - 0.5, W, 1);

    } else if (currentStyle === "filled") {
      // ── Smooth filled bezier area ──────────────────────────────────────────
      // Draw played region first, then unplayed
      const playedX = fraction * W;

      const drawFilledPath = (color: string, startBar: number, endBar: number) => {
        if (startBar >= endBar) return;
        const barWidth = W / numBars;

        ctx.beginPath();
        // Bottom-left start
        const x0 = startBar * barWidth;
        ctx.moveTo(x0, H);

        // Top curve (bezier through peak points)
        for (let i = startBar; i < endBar; i++) {
          const amplitude = peaks[i];
          const barH = Math.max(2, amplitude * H * 0.85);
          const x = i * barWidth + barWidth / 2;
          const y = H - barH;
          if (i === startBar) {
            ctx.lineTo(x, y);
          } else {
            const prevAmp = peaks[i - 1];
            const prevBarH = Math.max(2, prevAmp * H * 0.85);
            const px = (i - 1) * barWidth + barWidth / 2;
            const py = H - prevBarH;
            const cpx = (px + x) / 2;
            ctx.bezierCurveTo(cpx, py, cpx, y, x, y);
          }
        }

        // Bottom-right end
        ctx.lineTo((endBar - 1) * barWidth + barWidth / 2, H);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
      };

      // Unplayed region (behind)
      drawFilledPath(colorUnplayed, 0, numBars);
      // Played region (on top, clipped)
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, playedX, H);
      ctx.clip();
      drawFilledPath(colorPlayedAlpha, 0, numBars);
      ctx.restore();

      // Highlight line on top of played region
      if (fraction > 0) {
        const barWidth = W / numBars;
        ctx.beginPath();
        ctx.strokeStyle = colorPlayed;
        ctx.lineWidth = 2;
        for (let i = 0; i < playedBars && i < numBars; i++) {
          const amplitude = peaks[i];
          const barH = Math.max(2, amplitude * H * 0.85);
          const x = i * barWidth + barWidth / 2;
          const y = H - barH;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            const prevAmp = peaks[i - 1];
            const prevBarH = Math.max(2, prevAmp * H * 0.85);
            const px = (i - 1) * barWidth + barWidth / 2;
            const py = H - prevBarH;
            const cpx = (px + x) / 2;
            ctx.bezierCurveTo(cpx, py, cpx, y, x, y);
          }
        }
        ctx.stroke();
      }

    } else if (currentStyle === "line") {
      // ── Lollipop stems: thin vertical stems with circle dots at each peak ──
      const barWidth = W / numBars;
      const mid = H / 2;
      const playedX = fraction * W;
      const stemW = Math.max(0.5, barWidth * 0.18);
      const dotR = Math.max(1.5, barWidth * 0.42);

      // Subtle centre guideline
      ctx.strokeStyle = "rgba(128,128,128,0.12)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, mid);
      ctx.lineTo(W, mid);
      ctx.stroke();

      for (let i = 0; i < numBars; i++) {
        const amplitude = peaks[i];
        const stemH = Math.max(2, amplitude * H * 0.44);
        const x = i * barWidth + barWidth / 2;
        const isPlayed = x <= playedX;
        const color = isPlayed ? colorPlayed : colorUnplayed;

        // Top stem
        ctx.strokeStyle = color;
        ctx.lineWidth = stemW;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x, mid);
        ctx.lineTo(x, mid - stemH);
        ctx.stroke();

        // Bottom stem (faded mirror)
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.moveTo(x, mid);
        ctx.lineTo(x, mid + stemH);
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Top dot
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, mid - stemH, dotR, 0, Math.PI * 2);
        ctx.fill();

        // Bottom dot (faded mirror)
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.arc(x, mid + stemH, dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // ── Playhead cursor (all styles) ──────────────────────────────────────
    if (fraction > 0) {
      const px = fraction * W;
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.5)";
      ctx.shadowBlur = 4;
      ctx.fillRect(px - 1, 0, 2, H);
      ctx.shadowBlur = 0;
    }

    // ── Comment pin triangles ─────────────────────────────────────────────
    const pinned = commentsRef.current.filter(
      (c) => !c.isGlobal && c.timestampSeconds > 0
    );
    const tsv = totalSecondsRef.current;
    pinned.forEach((comment) => {
      if (!tsv) return;
      const px = (comment.timestampSeconds / tsv) * W;
      ctx.fillStyle = "#f97316";
      ctx.shadowColor = "rgba(0,0,0,0.4)";
      ctx.shadowBlur = 3;
      ctx.beginPath();
      ctx.moveTo(px - 4, 0);
      ctx.lineTo(px + 4, 0);
      ctx.lineTo(px, 8);
      ctx.closePath();
      ctx.fill();
      ctx.shadowBlur = 0;
    });
  }, []); // ← no deps: reads from refs only

  // ── Shared helpers: get live VU from analyser or fall back to static peaks ─
  // Returns value in VU units (0 VU = −18 dBFS), matching the main VuMeters component.
  const getLiveVu = (analyser: AnalyserNode | null, peakBin: number, peaks: Float32Array | null): number => {
    if (analyser && isVisualizerActiveRef.current && isThisTrackRef.current) {
      // Live: RMS+peak blend from last 256 samples — same as main VuMeters.getRmsDbfs()
      const bufLen = analyser.fftSize;
      const buf = new Float32Array(bufLen);
      analyser.getFloatTimeDomainData(buf);
      const win = Math.min(256, bufLen);
      const start = bufLen - win;
      let sum = 0, pkAmp = 0;
      for (let i = start; i < bufLen; i++) {
        const s = buf[i];
        sum += s * s;
        if (Math.abs(s) > pkAmp) pkAmp = Math.abs(s);
      }
      const rms = Math.sqrt(sum / win);
      // 60 % RMS + 40 % peak — punchy but not too spiky
      const blended = rms * 0.6 + pkAmp * 0.4;
      return 20 * Math.log10(Math.max(blended, 1e-10)) - VU_REF_DBFS;
    }
    if (peaks && isThisTrackRef.current && isPlayingRef.current && totalSecondsRef.current > 0) {
      // Static fallback: peak amplitude directly — no √2 attenuation so it's as
      // punchy as the live path when the analyser isn't available.
      const amp = peaks[peakBin];
      return 20 * Math.log10(Math.max(amp, 1e-6)) - VU_REF_DBFS;
    }
    return VU_MIN_VU - 3; // silence — below bottom of scale
  };

  // ── VU meter draw — reads from refs, no reactive deps ────────────────────
  const drawVu = useCallback(() => {
    const canvas = vuCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvas.width / dpr;
    const H = canvas.height / dpr;
    const now = Date.now();
    const mode = vuModeRef.current;

    // ── Shared: compute current playback bin from static peaks ───────────────
    let peakBin = 0;
    if (totalSecondsRef.current > 0) {
      const frac = Math.min(playedSecondsRef.current / totalSecondsRef.current, 1);
      const len = peaksLRef.current?.length ?? 1;
      peakBin = Math.min(Math.floor(frac * len), len - 1);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    // ── SCOPE mode ───────────────────────────────────────────────────────────
    if (mode === "scope") {
      const al = analyserLRef.current;
      const active = isVisualizerActiveRef.current && isThisTrackRef.current && !!al;
      ctx.fillStyle = "rgba(0,18,0,0.97)";
      ctx.fillRect(0, 0, W, H);
      if (active && al) {
        const buf = new Float32Array(al.fftSize);
        al.getFloatTimeDomainData(buf);
        ctx.strokeStyle = "#20e060";
        ctx.lineWidth = 1.5;
        ctx.shadowColor = "#20e060";
        ctx.shadowBlur = 4;
        ctx.beginPath();
        const step = W / buf.length;
        for (let i = 0; i < buf.length; i++) {
          const x = i * step;
          const y = (1 - (buf[i] + 1) / 2) * H;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      } else {
        // flat line when silent
        ctx.strokeStyle = "rgba(32,224,96,0.25)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    // ── SPECTRUM mode ────────────────────────────────────────────────────────
    if (mode === "spectrum") {
      const al = analyserLRef.current || analyserRRef.current;
      const active = isVisualizerActiveRef.current && isThisTrackRef.current && !!al;
      ctx.fillStyle = "rgba(0,10,0,0.97)";
      ctx.fillRect(0, 0, W, H);
      if (active && al) {
        const bins = al.frequencyBinCount;
        const freq = new Float32Array(bins);
        al.getFloatFrequencyData(freq);
        const BAR = Math.max(2, Math.floor(W / 64));
        const GAP = 1;
        const count = Math.floor(W / (BAR + GAP));
        for (let i = 0; i < count; i++) {
          const bin = Math.floor((i / count) * bins);
          const db = Math.max(al.minDecibels, freq[bin]);
          const ratio = (db - al.minDecibels) / (al.maxDecibels - al.minDecibels);
          const barH = ratio * H;
          const x = i * (BAR + GAP);
          const hue = 120 - ratio * 120; // green→yellow→red
          ctx.fillStyle = `hsl(${hue},100%,${40 + ratio * 25}%)`;
          ctx.fillRect(x, H - barH, BAR, barH);
        }
      } else {
        ctx.fillStyle = "rgba(32,200,60,0.12)";
        ctx.font = `10px monospace`;
        ctx.textAlign = "center";
        ctx.fillText("No signal", W / 2, H / 2 + 4);
      }
      ctx.restore();
      return;
    }

    // ── NEEDLE VU mode ───────────────────────────────────────────────────────
    if (mode === "needle") {
      const targetL = getLiveVu(analyserLRef.current, peakBin, peaksLRef.current);
      const targetR = getLiveVu(analyserRRef.current, peakBin, peaksRRef.current);
      const ATTACK = 0.35; const RELEASE = 0.08;
      vuLRef.current += (targetL - vuLRef.current) * (targetL > vuLRef.current ? ATTACK : RELEASE);
      vuRRef.current += (targetR - vuRRef.current) * (targetR > vuRRef.current ? ATTACK : RELEASE);

      ctx.fillStyle = "#0e1010";
      ctx.fillRect(0, 0, W, H);

      const drawNeedle = (cx: number, cw: number, db: number, label: string) => {
        const vu = Math.max(-20, Math.min(3, db)); // VU range -20 to +3
        const angle = ((vu + 20) / 23) * Math.PI * 0.75 - Math.PI * 0.375;
        const r = Math.min(cw * 0.46, H * 0.88);
        const ox = cx + cw / 2;
        const oy = H + r * 0.18;

        // Arc background
        ctx.strokeStyle = "rgba(60,60,60,0.8)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ox, oy, r, -Math.PI * 0.875, -Math.PI * 0.125);
        ctx.stroke();

        // Colored arc fill (green → yellow → red)
        const startA = -Math.PI * 0.875;
        const endA = startA + ((vu + 20) / 23) * Math.PI * 0.75;
        const grad = ctx.createLinearGradient(ox - r, oy, ox + r, oy);
        grad.addColorStop(0, "#20e060");
        grad.addColorStop(0.6, "#ffe020");
        grad.addColorStop(1, "#ff2020");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(ox, oy, r, startA, endA);
        ctx.stroke();

        // Needle
        const nx = ox + Math.cos(angle - Math.PI / 2) * r * 0.9;
        const ny = oy + Math.sin(angle - Math.PI / 2) * r * 0.9;
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(ox, oy);
        ctx.lineTo(nx, ny);
        ctx.stroke();

        // Labels — high contrast
        ctx.fillStyle = "rgba(230,230,230,1)";
        ctx.font = `bold ${Math.round(11 * dpr) / dpr}px monospace`;
        ctx.textAlign = "center";
        ctx.fillText(label, ox, H - 2);
        ctx.fillStyle = db > 0 ? "#ff5050" : "rgba(130,240,130,1)";
        ctx.font = `bold ${Math.round(9 * dpr) / dpr}px monospace`;
        ctx.fillText(`${db.toFixed(1)}`, ox, H - 14);
      };

      drawNeedle(0, W / 2 - 2, vuLRef.current, "L");
      drawNeedle(W / 2 + 2, W / 2 - 2, vuRRef.current, "R");
      ctx.restore();
      return;
    }

    // ── LED VU bars (default) — same calibration as main VuMeters component ──
    const targetL = getLiveVu(analyserLRef.current, peakBin, peaksLRef.current);
    const targetR = getLiveVu(analyserRRef.current, peakBin, peaksRRef.current);

    // IIR ballistics matched to main VuMeters (ATTACK=0.22, DECAY=0.08)
    const ATTACK = 0.22;
    const RELEASE = 0.08;
    vuLRef.current += (targetL - vuLRef.current) * (targetL > vuLRef.current ? ATTACK : RELEASE);
    vuRRef.current += (targetR - vuRRef.current) * (targetR > vuRRef.current ? ATTACK : RELEASE);

    const vuL = vuLRef.current;
    const vuR = vuRRef.current;

    // Peak hold (raw un-smoothed target for accurate peak)
    for (const [vu, peak] of [
      [targetL, peakLRef.current],
      [targetR, peakRRef.current],
    ] as [number, { db: number; heldAt: number }][]) {
      if (vu > peak.db) { peak.db = vu; peak.heldAt = now; }
      else if (now - peak.heldAt > PEAK_HOLD_MS) {
        peak.db -= PEAK_FALL_RATE;
        if (peak.db < VU_MIN_VU) peak.db = VU_MIN_VU;
      }
    }

    const labelW = 36;
    const meterAreaW = W - labelW;
    const gap = 4;
    const meterW = (meterAreaW - gap) / 2;
    const bottomH = 16;
    const segH = (H - bottomH) / VU_SEGMENTS;
    const segGap = 1.5;
    const segBarH = segH - segGap;

    const drawChannel = (
      x: number, vuVal: number,
      peak: { db: number; heldAt: number },
      label: string
    ) => {
      for (let s = 0; s < VU_SEGMENTS; s++) {
        const vu = segmentToVu(s);
        const lit = vuVal >= vu;
        const y = H - bottomH - (s + 1) * segH + segGap / 2;
        ctx.fillStyle = segmentColor(vu, lit);
        ctx.beginPath();
        ctx.roundRect(x, y, meterW, segBarH, 1.5);
        ctx.fill();
      }
      // Peak hold indicator
      if (peak.db > VU_MIN_VU) {
        const ps2 = Math.round(((peak.db - VU_MIN_VU) / VU_SPAN) * (VU_SEGMENTS - 1));
        const cs = Math.max(0, Math.min(VU_SEGMENTS - 1, ps2));
        const py = H - bottomH - (cs + 1) * segH + segGap / 2;
        ctx.fillStyle = segmentColor(segmentToVu(cs), true);
        ctx.globalAlpha = 0.95;
        ctx.fillRect(x, py, meterW, 2);
        ctx.globalAlpha = 1;
      }
      // Channel label — bold, full contrast
      ctx.fillStyle = "rgba(230,230,230,1)";
      ctx.font = `bold ${Math.round(11 * dpr) / dpr}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText(label, x + meterW / 2, H - 2);
    };

    drawChannel(labelW, vuL, peakLRef.current, "L");
    drawChannel(labelW + meterW + gap, vuR, peakRRef.current, "R");

    // VU scale labels (in VU units, matching main VuMeters)
    const scaleValues = [3, 0, -3, -7, -10, -20];
    ctx.font = `bold ${Math.round(9 * dpr) / dpr}px monospace`;
    ctx.textAlign = "right";
    for (const vuMark of scaleValues) {
      const ratio = (vuMark - VU_MIN_VU) / VU_SPAN;
      const seg = Math.round(ratio * (VU_SEGMENTS - 1));
      const y = H - bottomH - (seg + 0.5) * segH;
      if (y < 0 || y > H - bottomH) continue;
      if (vuMark >= 3)  ctx.fillStyle = "rgba(255,80,80,1)";
      else if (vuMark >= 0)  ctx.fillStyle = "rgba(255,200,80,1)";
      else if (vuMark >= -3) ctx.fillStyle = "rgba(255,240,100,1)";
      else ctx.fillStyle = "rgba(180,200,180,1)";
      const lbl = vuMark > 0 ? `+${vuMark}` : (vuMark === 0 ? "0" : String(vuMark));
      ctx.fillText(lbl, labelW - 3, y + 3);
    }
    // "VU" unit label
    ctx.fillStyle = "rgba(160,180,160,0.95)";
    ctx.font = `bold ${Math.round(8 * dpr) / dpr}px monospace`;
    ctx.fillText("VU", labelW - 3, H - bottomH - 2);

    ctx.restore();
  }, []); // ← no deps: reads from refs only

  // ── Resize: waveform canvas ───────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const resize = () => {
      const canvas = waveCanvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = height * dpr;
      const c = canvas.getContext("2d");
      if (c) c.scale(dpr, dpr);
      drawWaveform();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();
    return () => ro.disconnect();
  }, [height, drawWaveform]);

  // ── Resize: VU canvas ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!showVu) return;
    const container = containerRef.current;
    if (!container) return;
    const resize = () => {
      const canvas = vuCanvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = vuHeight * dpr;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();
    return () => ro.disconnect();
  }, [showVu, vuHeight]);

  // ── Waveform RAF (runs once decode is ready, never restarts) ─────────────
  useEffect(() => {
    if (decodeState !== "ready") return;
    const loop = () => {
      drawWaveform();
      waveRafRef.current = requestAnimationFrame(loop);
    };
    waveRafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(waveRafRef.current);
  }, [decodeState, drawWaveform]);

  // ── VU RAF (always running when visible, never restarts) ─────────────────
  useEffect(() => {
    if (!showVu) return;
    const loop = () => {
      drawVu();
      vuRafRef.current = requestAnimationFrame(loop);
    };
    vuRafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(vuRafRef.current);
  }, [showVu, drawVu]);

  // ── Click to seek ─────────────────────────────────────────────────────────
  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!totalSecondsRef.current) return;
      const canvas = waveCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      seek(Math.max(0, Math.min(1, frac)) * totalSecondsRef.current);
    },
    [seek]
  );

  // ── Hover time tooltip ────────────────────────────────────────────────────
  const [hoverTime, setHoverTime] = useState<string | null>(null);
  const [hoverX, setHoverX] = useState(0);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const ts = totalSecondsRef.current;
      if (!ts) return;
      const canvas = waveCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      const secs = Math.max(0, Math.min(1, frac)) * ts;
      const m = Math.floor(secs / 60);
      const s = Math.floor(secs % 60);
      setHoverTime(`${m}:${s.toString().padStart(2, "0")}`);
      setHoverX(e.clientX - rect.left);
    },
    []
  );

  const handleMouseLeave = useCallback(() => setHoverTime(null), []);

  // ── Comment pin tooltip ───────────────────────────────────────────────────
  const [pinTooltip, setPinTooltip] = useState<{ text: string; x: number } | null>(
    null
  );

  const handleMouseMoveForPins = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const ts = totalSecondsRef.current;
      if (!ts) return;
      const canvas = waveCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      const secs = frac * ts;
      const THRESHOLD = ts * 0.015;
      const near = commentsRef.current.find(
        (c) =>
          !c.isGlobal && c.timestampSeconds > 0 &&
          Math.abs(c.timestampSeconds - secs) < THRESHOLD
      );
      if (near) {
        setPinTooltip({
          text: `${near.userName}: ${near.content.slice(0, 50)}${near.content.length > 50 ? "…" : ""} [${formatCommentTimestamp(near.timestampSeconds)}]`,
          x: e.clientX - rect.left,
        });
      } else {
        setPinTooltip(null);
      }
    },
    []
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={containerRef} className={cn("relative w-full select-none", className)}>
      {/* Style selector — above the waveform, flush right */}
      {decodeState === "ready" && showStylePicker && (
        <div className="flex justify-end gap-0.5 mb-1">
          {STYLE_LABELS.map(({ style, label, title }) => (
            <button
              key={style}
              title={title}
              onClick={() => setWaveStyle(style)}
              className={cn(
                "text-[11px] font-mono px-1.5 py-0.5 rounded transition-colors",
                waveStyle === style
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {decodeState === "loading" && (
        <div
          className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-muted-foreground"
          style={{ height }}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {showStylePicker && <span>Decoding waveform…</span>}
        </div>
      )}

      {decodeState === "idle" && (
        <div
          className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-muted-foreground"
          style={{ height }}
        >
          <AudioLines className="h-4 w-4" />
        </div>
      )}

      {decodeState === "error" && (
        <div
          className="flex items-center justify-center gap-2 text-xs text-muted-foreground"
          style={{ height }}
        >
          <AudioLines className="h-4 w-4 opacity-40" />
          <span className="opacity-60">Waveform unavailable</span>
        </div>
      )}

      <canvas
        ref={waveCanvasRef}
        style={{
          width: "100%",
          height,
          cursor: "pointer",
          opacity: decodeState === "ready" ? 1 : 0,
          transition: "opacity 0.3s ease",
        }}
        onClick={handleCanvasClick}
        onMouseMove={(e) => { handleMouseMove(e); handleMouseMoveForPins(e); }}
        onMouseLeave={handleMouseLeave}
        aria-label="Audio waveform — click to seek"
      />

      {hoverTime && !pinTooltip && (
        <div
          className="pointer-events-none absolute -top-6 text-[10px] font-mono px-1.5 py-0.5 rounded bg-background border border-border shadow text-foreground"
          style={{ left: Math.max(0, hoverX - 16), transform: "translateX(-50%)" }}
        >
          {hoverTime}
        </div>
      )}

      {pinTooltip && (
        <div
          className="pointer-events-none absolute -top-8 text-[10px] px-2 py-1 rounded bg-orange-500 text-white shadow max-w-[200px] truncate"
          style={{ left: Math.max(0, pinTooltip.x - 16) }}
        >
          {pinTooltip.text}
        </div>
      )}

      {/* Timestamp labels */}
      {decodeState === "ready" && (
        <div className="flex justify-between text-[10px] text-muted-foreground font-mono mt-0.5 px-0.5">
          <span>{formatCommentTimestamp(playedSeconds) || "0:00"}</span>
          <span>{formatCommentTimestamp(totalSeconds)}</span>
        </div>
      )}

      {/* VU meters */}
      {showVu && decodeState === "ready" && (
        <div className="mt-2">
          <div className="text-[9px] uppercase tracking-widest text-muted-foreground/50 mb-1 pl-[26px]">
            VU
          </div>
          <canvas
            ref={vuCanvasRef}
            style={{ width: "100%", height: `${vuHeight}px`, display: "block" }}
          />
        </div>
      )}
    </div>
  );
}
